import { differenceInMilliseconds } from 'date-fns';
import { forEach } from 'lodash-es';

import {
  IClosestSlide, ICurrentSlidePosition, ISlidePosition, ISwiperState,
} from '../types/ISwiper';

// minimalny czas od którego akcja jest traktowana jako swipe (ruch myszka, palcem na mobile)
// ponizej 35ms akcja nie jest traktowana jako swipe (zmiana slajdu)
const swipeTimeTreshold = 35;
// maksymalny czas swipe'a w którym może zostać wykonane przewinięcie więcej niż jednego slajdu, mocnym przeciągnięciem
const swipeMaxDuration = 300;
// Czułość swipe'a przeskakiwanie o kilka slajdow za jednym machnięciem.
// im wieksza cyfra tym większa czułość czyli łatwiej jest przeskakiwac o wieksza ilośc slajdów
const swipeSensitivity = 2.5;

function getClosestSlide(swiperState: ISwiperState): IClosestSlide {
  let smallestValue = 1000000;
  let closestSlide: HTMLElement | null = null;
  let closestSlideIndex: number = 0;

  forEach(swiperState.slideElements, (slide, slideIndex) => {
    const absoluteSlidePosition = Math.abs(swiperState.slidesPositions[slideIndex] + swiperState.swiperPosition);

    if (absoluteSlidePosition < smallestValue) {
      smallestValue = absoluteSlidePosition;
      closestSlide = slide;
      closestSlideIndex = slideIndex;
    }
  });
  if (closestSlide === null) {
    throw new Error('Slide Not Found: swiper-r.vue');
  }

  return { slide: closestSlide, value: smallestValue, slideIndex: closestSlideIndex };
}

function getStartEndBoolean(index: number, swiperState: ISwiperState): ISlidePosition {
  return {
    isStart: index <= 0,
    isEnd: index >= swiperState.slidesPositions?.length - 1,
    index,
  };
}

export function snapSlide(slideIndex: number, swiperState: ISwiperState): void {
  swiperState.currentSlideIndex = slideIndex;
  swiperState.swiperPosition = -swiperState.slidesPositions[slideIndex];
  swiperState.emit('change', getStartEndBoolean(slideIndex, swiperState));
  setTimeout(() => {}, swiperState.transitionDuration);
}

export function prevSlide(swiperState: ISwiperState, step = 1): ICurrentSlidePosition {
  const startEnd = getStartEndBoolean(swiperState.currentSlideIndex - step, swiperState);
  const prevSlideIndex = startEnd.isStart ? 0 : swiperState.currentSlideIndex - step;

  snapSlide(prevSlideIndex, swiperState);
  return { ...startEnd, currentSlideIndex: swiperState.currentSlideIndex };
}

export function nextSlide(swiperState: ISwiperState, step = 1): ICurrentSlidePosition {
  const startEnd = getStartEndBoolean(swiperState.currentSlideIndex + step, swiperState);
  const nextSlideIndex = startEnd.isEnd ? swiperState.slidesPositions?.length - 1 : swiperState.currentSlideIndex + step;

  snapSlide(nextSlideIndex, swiperState);
  return { ...startEnd, currentSlideIndex: swiperState.currentSlideIndex };
}

export function moveSwiper(clientX: number, swiperState: ISwiperState): void {
  if (swiperState.moveFinished) {
    return;
  }

  const relativeCursorPosition = clientX - swiperState.parentX;

  swiperState.lastTouchPosition = clientX;
  const relativeMouseOffset = relativeCursorPosition
    - (swiperState.mousePositionStart - swiperState.parentX)
    - swiperState.slideElements[swiperState.currentSlideIndex].offsetLeft;

  swiperState.swiperPosition = relativeMouseOffset;
}

export function handleSwipe(clientX: number, swiperState: ISwiperState) {
  const swipeTime = Math.abs(differenceInMilliseconds(swiperState.swipeStartTime, Date.now()));

  if (swipeTimeTreshold < swipeTime && swipeTime < swipeMaxDuration) {
    const deltaX = swiperState.mousePositionStart - clientX;
    const acceleration = Math.abs(deltaX / (swipeTime / swipeSensitivity));
    let step = Math.sqrt(acceleration / 2);

    step = Math.round(step);
    if (deltaX > 0) {
      nextSlide(swiperState, step);
    } else {
      prevSlide(swiperState, step);
    }
  }

  const closestSlide = getClosestSlide(swiperState);

  snapSlide(closestSlide.slideIndex, swiperState);
}
